import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import type { Tenant } from './ClientsListPage';
import ClientUsersTab from './components/ClientUsersTab';
import ClientDevicesTab from './components/ClientDevicesTab';
import ClientDomainsTab from './components/ClientDomainsTab';
import ClientModuleAccessTab from './components/ClientModuleAccessTab';
import ClientSecurityTab from './components/ClientSecurityTab';
import ClientMqttTab from './components/ClientMqttTab';

type Tab = 'overview' | 'users' | 'devices' | 'domains' | 'modules' | 'security' | 'mqtt';

interface Props {
  tenant: Tenant;
  onBack: () => void;
  onTenantUpdated: (tenant: Tenant) => void;
}

export default function ClientDetailPage({ tenant, onBack, onTenantUpdated }: Props) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<Tab>('overview');

  // Renombrar SOLO cambia `name`, nunca el slug: el slug identifica al tenant
  // en el ruteo por dominio y en las políticas de RLS, así que cambiarlo
  // rompería el acceso del cliente. Por eso PUT /admin/tenants/:id tampoco lo
  // acepta — el nombre es la etiqueta que se le muestra a la gente, el slug es
  // la llave técnica.
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft]       = useState(tenant.name);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const openRename = () => { setDraft(tenant.name); setError(''); setRenaming(true); };

  // El slug SÍ se puede cambiar, salvo 'demo' y 'leyer' que el backend
  // rechaza porque viven hardcodeados en el código y en los seeds. No lo usa
  // ni el ruteo por dominio (va por tenant_domains.host → id) ni el
  // aislamiento entre clientes (va por UUID).
  const [editingSlug, setEditingSlug] = useState(false);
  const [slugDraft, setSlugDraft]     = useState(tenant.slug);
  const [slugSaving, setSlugSaving]   = useState(false);
  const [slugError, setSlugError]     = useState('');

  const saveSlug = async () => {
    const slug = slugDraft.trim().toLowerCase();
    if (!slug || slug === tenant.slug) { setEditingSlug(false); return; }
    setSlugSaving(true);
    setSlugError('');
    try {
      const { data } = await api.put(`/admin/tenants/${tenant.id}`, { slug });
      onTenantUpdated({ ...tenant, slug: data.slug ?? slug });
      setEditingSlug(false);
    } catch (err: any) {
      setSlugError(err.response?.data?.error ?? t('admin.detail.slugError'));
    } finally {
      setSlugSaving(false);
    }
  };

  const saveName = async () => {
    const name = draft.trim();
    if (!name || name === tenant.name) { setRenaming(false); return; }
    setSaving(true);
    setError('');
    try {
      const { data } = await api.put(`/admin/tenants/${tenant.id}`, { name });
      onTenantUpdated({ ...tenant, name: data.name ?? name });
      setRenaming(false);
    } catch (err: any) {
      setError(err.response?.data?.error ?? t('admin.detail.renameError'));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    const { data } = await api.put(`/admin/tenants/${tenant.id}`, { active: !tenant.active });
    onTenantUpdated({ ...tenant, active: data.active });
  };

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: t('admin.detail.tabs.overview') },
    { key: 'users',    label: t('users.title') },
    { key: 'devices',  label: t('devices.title') },
    { key: 'domains',  label: t('domains.title') },
    { key: 'modules',  label: t('admin.moduleAccess.title') },
    { key: 'security', label: t('admin.security.title') },
    { key: 'mqtt',     label: t('admin.mqtt.tabLabel') },
  ];

  return (
    <div className="page">
      <button
        onClick={onBack}
        style={{ fontSize: 12, color: 'var(--gray)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, marginBottom: 16 }}
      >
        ← {t('admin.detail.back')}
      </button>

      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          {renaming ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <input
                autoFocus
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') saveName();
                  if (e.key === 'Escape') setRenaming(false);
                }}
                maxLength={120}
                style={{ fontSize: 18, fontWeight: 600, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, outline: 'none', minWidth: 220 }}
              />
              <button onClick={saveName} disabled={saving} className="primary-button" style={{ padding: '6px 14px', fontSize: 12 }}>
                {saving ? t('admin.detail.renameSaving') : t('admin.detail.renameSave')}
              </button>
              <button
                onClick={() => setRenaming(false)}
                style={{ padding: '6px 12px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', cursor: 'pointer' }}
              >
                {t('tenants.form.cancel')}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>{tenant.name}</h1>
              <button
                onClick={openRename}
                title={t('admin.detail.rename')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, lineHeight: 1, display: 'flex', alignItems: 'center', opacity: 0.4 }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--dark)' }}>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
            </div>
          )}
          {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 4 }}>{error}</div>}
          <p style={{ fontSize: 13, color: 'var(--gray)' }}>
            /{tenant.slug} · {t('tenants.users', { count: tenant.userCount })} · {t('tenants.since')} {new Date(tenant.createdAt).toLocaleDateString(i18n.language.startsWith('es') ? 'es-MX' : 'en-US', { month: 'short', year: 'numeric' })}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{
            fontSize: 11, padding: '2px 10px', borderRadius: 99, fontWeight: 600,
            background: tenant.active ? 'var(--green-bg)' : '#f5f5f3',
            color: tenant.active ? 'var(--green-dk)' : 'var(--gray)',
          }}>
            {tenant.active ? t('tenants.status.active') : t('tenants.status.inactive')}
          </span>
          <button
            onClick={toggleActive}
            style={{ fontSize: 11, padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--gray)', cursor: 'pointer' }}
          >
            {tenant.active ? t('tenants.suspend') : t('tenants.activate')}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {tabs.map(tb => (
          <button
            key={tb.key}
            onClick={() => setTab(tb.key)}
            style={{
              padding: '9px 16px', fontSize: 13, fontWeight: tab === tb.key ? 600 : 400,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: tab === tb.key ? 'var(--green-dk)' : 'var(--gray)',
              borderBottom: tab === tb.key ? '2px solid var(--green)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {tb.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 480 }}>
          <OverviewRow label={t('admin.detail.tabs.overview')} value={tenant.name} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '14px 18px', background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--gray)' }}>{t('tenants.form.slug')}</span>
            {editingSlug ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  autoFocus
                  value={slugDraft}
                  onChange={e => setSlugDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  onKeyDown={e => {
                    if (e.key === 'Enter') saveSlug();
                    if (e.key === 'Escape') setEditingSlug(false);
                  }}
                  maxLength={100}
                  style={{ fontSize: 13, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, outline: 'none', fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace' }}
                />
                <button onClick={saveSlug} disabled={slugSaving} className="primary-button" style={{ padding: '5px 12px', fontSize: 12 }}>
                  {slugSaving ? t('admin.detail.renameSaving') : t('admin.detail.renameSave')}
                </button>
                <button
                  onClick={() => setEditingSlug(false)}
                  style={{ padding: '5px 10px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 8, background: 'transparent', cursor: 'pointer' }}
                >
                  {t('tenants.form.cancel')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)' }}>{tenant.slug}</span>
                <button
                  onClick={() => { setSlugDraft(tenant.slug); setSlugError(''); setEditingSlug(true); }}
                  title={t('admin.detail.editSlug')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, lineHeight: 1, display: 'flex', alignItems: 'center', opacity: 0.4 }}
                  onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                  onMouseLeave={e => { e.currentTarget.style.opacity = '0.4'; }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--dark)' }}>
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          {slugError && <div style={{ fontSize: 12, color: 'var(--red)', marginTop: -4, marginBottom: 8 }}>{slugError}</div>}
          <OverviewRow label={t('tenants.usersModal.title', { tenant: '' })} value={String(tenant.userCount)} />
          <OverviewRow label={t('admin.moduleAccess.title')} value={t('admin.detail.modulesCount', { count: tenant.enabledModules.length, total: 8 })} />
        </div>
      )}
      {tab === 'users' && <ClientUsersTab tenantId={tenant.id} tenantName={tenant.name} />}
      {tab === 'devices' && <ClientDevicesTab tenantId={tenant.id} />}
      {tab === 'domains' && <ClientDomainsTab tenantId={tenant.id} tenantName={tenant.name} />}
      {tab === 'modules' && (
        <ClientModuleAccessTab
          tenantId={tenant.id}
          enabledModules={tenant.enabledModules}
          onTenantUpdated={(patch) => onTenantUpdated({ ...tenant, ...patch })}
        />
      )}
      {tab === 'security' && (
        <ClientSecurityTab
          tenantId={tenant.id}
          requireMfa={tenant.requireMfa}
          onTenantUpdated={(patch) => onTenantUpdated({ ...tenant, ...patch })}
        />
      )}
      {tab === 'mqtt' && <ClientMqttTab tenantId={tenant.id} tenantName={tenant.name} />}
    </div>
  );
}

function OverviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--white)', borderRadius: 10, border: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--gray)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500 }}>{value}</span>
    </div>
  );
}
